/**
 * Projects an activated program onto calendar dates.
 * Same rules as the athlete app `src/lib/calendar/programSchedule.ts`.
 */

import { toISODate } from '@/lib/athleteCalendar';

const LAST_WEEKDAY = 7;
const DAYS_PER_WEEK = 7;

function isoWeekday(date) {
  return ((date.getDay() + 6) % 7) + 1;
}

function parseTimestamp(value) {
  const parsed = new Date(String(value).replace(/(\.\d{3})\d+/, '$1'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function activationDay(activatedAt) {
  const activation = parseTimestamp(activatedAt);
  if (!activation) return null;
  return new Date(activation.getFullYear(), activation.getMonth(), activation.getDate());
}

export function weekOneMonday(activatedAt) {
  const localDay = activationDay(activatedAt);
  if (!localDay) return null;

  const weekday = isoWeekday(localDay);
  localDay.setDate(localDay.getDate() + (weekday === 1 ? 0 : DAYS_PER_WEEK + 1 - weekday));
  return localDay;
}

function groupByWeek(sessions) {
  const weeks = new Map();

  sessions.forEach((session) => {
    if (session.weekNumber == null) return;
    weeks.set(session.weekNumber, [...(weeks.get(session.weekNumber) ?? []), session]);
  });

  weeks.forEach((weekSessions, week) => {
    weeks.set(
      week,
      [...weekSessions].sort((a, b) => {
        const left = a.sessionNumber ?? Number.MAX_SAFE_INTEGER;
        const right = b.sessionNumber ?? Number.MAX_SAFE_INTEGER;
        return left === right ? a.id.localeCompare(b.id) : left - right;
      })
    );
  });

  return weeks;
}

function placeSessions(sessions) {
  const placements = [];

  groupByWeek(sessions).forEach((weekSessions, weekNumber) => {
    let fallbackOffset = 0;

    weekSessions.forEach((session) => {
      let weekday;
      if (session.dayOfWeek != null) {
        weekday = session.dayOfWeek;
      } else {
        weekday = Math.min(fallbackOffset + 1, LAST_WEEKDAY);
        fallbackOffset += 1;
      }

      placements.push({
        sessionId: session.id,
        weekNumber,
        offsetDays: (weekNumber - 1) * DAYS_PER_WEEK + (weekday - 1),
      });
    });
  });

  return placements;
}

function scheduleAnchor(activation, placements) {
  if (activation.startMode !== 'immediate') return weekOneMonday(activation.activatedAt);

  const anchor = activationDay(activation.activatedAt);
  if (!anchor || placements.length === 0) return anchor;

  const earliest = placements.reduce(
    (min, placement) => Math.min(min, placement.offsetDays),
    Number.MAX_SAFE_INTEGER
  );
  anchor.setDate(anchor.getDate() - earliest);
  return anchor;
}

export function projectProgramSessions(activation, sessions) {
  const placements = placeSessions(sessions);
  const anchor = scheduleAnchor(activation, placements);
  if (!anchor) return [];

  const expiry = activation.expiresAt ? parseTimestamp(activation.expiresAt) : null;
  const expiryDate = expiry ? toISODate(expiry) : null;
  const projected = [];

  placements.forEach((placement) => {
    const date = new Date(anchor);
    date.setDate(date.getDate() + placement.offsetDays);
    const isoDate = toISODate(date);
    if (expiryDate && isoDate > expiryDate) return;

    projected.push({
      date: isoDate,
      sessionId: placement.sessionId,
      programId: activation.programId,
      weekNumber: placement.weekNumber,
    });
  });

  return projected;
}

export function firstProjectedDate(activation, sessions) {
  return projectProgramSessions(activation, sessions).reduce(
    (earliest, projection) =>
      earliest === null || projection.date < earliest ? projection.date : earliest,
    null
  );
}

export function formatStartDateLabel(date, locale = 'en') {
  let day;
  if (typeof date === 'string') {
    const [year, month, dayOfMonth] = date.split('-').map(Number);
    day = new Date(year, month - 1, dayOfMonth, 12);
  } else {
    day = date;
  }

  return day.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

export function hasFutureSessions(activation, sessions, fromDate) {
  return projectProgramSessions(activation, sessions).some(
    (projection) => projection.date >= fromDate
  );
}

export function buildProgramScheduleMap(activations, sessionsByProgram, range) {
  const byDate = new Map();

  activations.forEach((activation) => {
    const sessions = sessionsByProgram[activation.programId] ?? [];
    projectProgramSessions(activation, sessions).forEach((projection) => {
      if (projection.date < range.from || projection.date > range.to) return;
      byDate.set(projection.date, [...(byDate.get(projection.date) ?? []), projection]);
    });
  });

  return byDate;
}
